/**
 * What a screen shows when it fails, instead of React Router's developer page.
 *
 * The commonest failure is not a bug but a deploy. Screens like the admin portal
 * load as separate files named by their content, and a release replaces them.
 * A phone that opened SmartSpaza before the release still holds the old page,
 * which asks for files that no longer exist — Safari says "Importing a module
 * script failed" — and a shopkeeper was shown "Hey developer 👋".
 *
 * So a stale file reloads the app once, which fetches the new version, and
 * anything else gets a plain explanation with a way out.
 */

import { RefreshCw } from "lucide-react";
import { useRouteError } from "react-router-dom";

import { Button } from "@/components/ui";

import { isStokkiePath } from "@/features/stokkie/useStokkieTitle";

import { isStaleFileError, reloadForNewVersion } from "./staleVersion";

export function RouteError() {
  const error = useRouteError();
  const stale = isStaleFileError(error);
  // A stokvel member never signed up to "SmartSpaza", and a till has no use
  // for Stokkie Stokvel: each side's error says its own name and goes to its own start.
  const stokkie = isStokkiePath(window.location.pathname);
  const name = stokkie ? "Stokkie Stokvel" : "SmartSpaza";

  // Rendering this is enough to start the reload; the card shows only if the
  // guard says it has already been tried.
  if (stale && reloadForNewVersion()) return null;

  return (
    <div className="grid min-h-screen place-items-center bg-canvas p-4">
      <div className="w-full max-w-md rounded-card bg-surface p-6 text-center shadow-pop">
        <h1 className="text-lg font-semibold text-ink-900">
          {stale ? `${name} has been updated` : "Something went wrong"}
        </h1>
        <p className="mt-2 text-sm text-ink-600">
          {stale
            ? "This screen is from the previous version. Reload to get the new one — nothing you saved is lost."
            : stokkie
              ? "This screen could not be shown. Reload to try again."
              : "This screen could not be shown. Reload to try again. Sales already made are safe on this device."}
        </p>
        <div className="mt-5 flex justify-center gap-2">
          <Button variant="primary" onClick={() => window.location.reload()}>
            <RefreshCw className="size-4" aria-hidden />
            Reload
          </Button>
          <Button
            variant="ghost"
            onClick={() => window.location.assign(stokkie ? "/stokvel" : "/")}
          >
            Go to start
          </Button>
        </div>
      </div>
    </div>
  );
}
